import React, { useEffect, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useNotifications } from "../../context/NotifContext";
import { useT } from "../../context/LanguageContext";
import {
  ChevronLeft, Bell, BellOff, CheckCheck, Trophy, MessageCircle,
  AlertTriangle, Calendar as CalendarIcon, UserPlus, Sparkles, Coins,
  ChevronRight,
} from "lucide-react";

/**
 * V2 / Mobile-native notifications inbox.
 * Reuses the global NotifContext (already polled every 60s) so we never
 * double-poll the endpoint. Renders at /v2/notifications.
 */
export default function NotificationsV2() {
  const { items, unread, markAllRead, markOneRead, fetchNow } = useNotifications();
  const { t } = useT();
  const nav = useNavigate();

  // Refresh once on mount so the inbox is fresh even if the global poller
  // hasn't fired in the last minute.
  useEffect(() => { fetchNow(); }, [fetchNow]);

  const sorted = useMemo(() => {
    return [...(items || [])].sort((a, b) => {
      const ta = new Date(a.created_at || 0).getTime();
      const tb = new Date(b.created_at || 0).getTime();
      return tb - ta;
    });
  }, [items]);

  return (
    <div className="px-5 pt-5 pb-6 space-y-4" data-testid="v2-notifications">
      <button
        type="button"
        onClick={() => nav(-1)}
        className="inline-flex items-center gap-1.5 text-[#A1A1AA] hover:text-white text-xs font-mono uppercase tracking-[0.25em] transition-colors"
        data-testid="v2-notif-back"
      >
        <ChevronLeft className="w-4 h-4" /> {t("v2.match.back")}
      </button>

      <header className="flex items-end justify-between gap-3">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.25em] text-[#A5B4FC]">{t("v2.notif.kicker")}</div>
          <h1 className="text-2xl font-bold text-white mt-1">{t("v2.notif.title")}</h1>
        </div>
        {unread > 0 && (
          <button
            type="button"
            onClick={markAllRead}
            data-testid="v2-notif-mark-all"
            className="inline-flex items-center gap-1.5 rounded-full bg-[#7C3AED]/15 border border-[#7C3AED]/40 text-[#A5B4FC] px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest hover:bg-[#7C3AED]/25 transition-colors"
          >
            <CheckCheck className="w-3 h-3" /> {t("v2.notif.markAll")}
          </button>
        )}
      </header>

      {sorted.length === 0 ? (
        <div className="rounded-2xl bg-[#12121C] border border-dashed border-[#1F1F2A] p-10 flex flex-col items-center text-center" data-testid="v2-notif-empty">
          <BellOff className="w-8 h-8 text-[#52525B] mb-2" />
          <div className="text-sm text-[#71717A]">{t("v2.notif.empty.title")}</div>
          <p className="text-xs text-[#52525B] mt-1 max-w-xs">{t("v2.notif.empty.body")}</p>
        </div>
      ) : (
        <ul className="space-y-1.5" data-testid="v2-notif-list">
          {sorted.map((n) => (
            <NotifRow key={n.id} n={n} onClick={() => { if (!n.read) markOneRead(n.id); }} t={t} />
          ))}
        </ul>
      )}
    </div>
  );
}

function NotifRow({ n, onClick, t }) {
  const cfg = iconFor(n.type, n);
  const Icon = cfg.icon;
  const dest = destFor(n);

  const inner = (
    <div className={`flex items-start gap-3 rounded-2xl px-3 py-3 transition-colors ${
      n.read
        ? "bg-[#12121C] border border-[#1F1F2A] hover:bg-[#161620]"
        : "bg-[#7C3AED]/8 border border-[#7C3AED]/30 hover:bg-[#7C3AED]/12"
    }`}
    >
      <div
        className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center"
        style={{ backgroundColor: `${cfg.color}22`, color: cfg.color, border: `1px solid ${cfg.color}44` }}
      >
        <Icon className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <div className={`text-sm leading-snug ${n.read ? "text-[#A1A1AA]" : "text-white"}`}>
            {n.title || t("v2.notif.fallback")}
          </div>
          {!n.read && <span className="flex-shrink-0 mt-1 w-2 h-2 rounded-full bg-[#7C3AED]" data-testid={`v2-notif-unread-${n.id}`} />}
        </div>
        {n.body && (
          <p className="text-xs text-[#71717A] mt-0.5 line-clamp-2">{n.body}</p>
        )}
        <div className="flex items-center gap-1.5 mt-1.5 text-[9px] font-mono uppercase tracking-widest text-[#52525B]">
          {fmtTs(n.created_at)}
        </div>
      </div>
      {dest && <ChevronRight className="w-4 h-4 text-[#52525B] flex-shrink-0 mt-1" />}
    </div>
  );

  if (dest) {
    return (
      <li>
        <Link
          to={dest}
          onClick={onClick}
          data-testid={`v2-notif-row-${n.id}`}
        >
          {inner}
        </Link>
      </li>
    );
  }
  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        data-testid={`v2-notif-row-${n.id}`}
        className="w-full text-left"
      >
        {inner}
      </button>
    </li>
  );
}

/** Pick an icon + accent color per notification type. Falls back to a generic
 *  bell when we receive a type we haven't met yet — no crash. */
function iconFor(type) {
  switch (type) {
    case "match_confirmed":
    case "match_pending":
      return { icon: Trophy, color: "#10B981" };
    case "match_disputed":
      return { icon: AlertTriangle, color: "#E11D48" };
    case "match_scheduled":
    case "reschedule_proposed":
    case "reschedule_accepted":
      return { icon: CalendarIcon, color: "#F59E0B" };
    case "dm":
    case "dm_new":
    case "league_chat":
    case "league_chat_new":
    case "pair_chat":
    case "classics_chat":
    case "friendlies_chat":
    case "proclubs_chat":
    case "wctourney_chat":
    case "wctourney_group_chat":
      return { icon: MessageCircle, color: "#A5B4FC" };
    case "follow_new":
      return { icon: UserPlus, color: "#22D3EE" };
    case "skin_unlocked":
    case "skin_redeem":
      return { icon: Sparkles, color: "#FFD700" };
    case "tokens_credited":
      return { icon: Coins, color: "#FBBF24" };
    default:
      return { icon: Bell, color: "#A1A1AA" };
  }
}

/** Map notification → V2-native destination.
 *  Strategy: prefer parsing the backend `link` (e.g. /chat/:peer, /h2h/:user,
 *  /classics?tab=chat&game=X) and rewrite it to its V2 equivalent so a chat
 *  notification always opens the conversation directly.
 *  Falls back to type-specific fields (match_id, league_id, etc.) for older
 *  notifications and ultimately to null = collapses to a mark-read tap. */
function destFor(n) {
  // ---- chat-type notifications: rewrite the V1 link to V2 -----------------
  const link = (n.link || "").trim();
  if (link) {
    // DM: /chat/:peerId  →  /v2/chat/:peerId
    let m = link.match(/^\/chat\/([^/?#]+)/);
    if (m) return `/v2/chat/${m[1]}`;
    // Pair chat (H2H): /h2h/:userId  →  /v2/h2h/:userId
    m = link.match(/^\/h2h\/([^/?#]+)/);
    if (m) return `/v2/h2h/${m[1]}`;
    // Classics chat lives only in V1 — fall back to V1 deep-link.
    if (link.startsWith("/classics")) return link;
    // Pro Clubs fixture chat: /proclubs/fixtures/:id → /v2/proclubs/fixtures/:id
    m = link.match(/^\/proclubs\/fixtures\/([^/?#]+)/);
    if (m) return `/v2/proclubs/fixtures/${m[1]}`;
    // Friendlies match deep-link → V2 pool page
    m = link.match(/^\/friendlies\/?([^/?#]*)/);
    if (m && m[1]) return `/v2/friendlies/${m[1]}`;
    // WC tourney chat (rare ntype): /world-cup-tourney/:platform
    m = link.match(/^\/world-cup-tourney\/([^/?#]+)/);
    if (m) return `/v2/world-cup-tourney/${m[1]}`;
  }
  // ---- legacy field-based routing (older notification shapes) -------------
  if (n.match_id) return `/v2/matches/${n.match_id}`;
  if (n.opponent_id) return `/v2/h2h/${n.opponent_id}`;
  if (n.peer_id || n.from_user_id) return `/v2/chat/${n.peer_id || n.from_user_id}`;
  if (n.league_id) return `/v2/leagues/${n.league_id}`;
  if (n.actor_username) return `/v2/players/${encodeURIComponent(n.actor_username)}`;
  if (n.type === "skin_unlocked" || n.type === "skin_redeem" || n.type === "tokens_credited") return "/v2/store";
  return null;
}

function fmtTs(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch {
    return iso;
  }
}
